import _startCase from "lodash/startCase";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Icon, Popup } from "semantic-ui-react";

import { CustomerCard, QuestionStatusIcon, ReactTable, TableDatePicker, TableDropdownSelector, TableQuestionStatusButton, useCan } from "components";
import { usePermanentLocations } from "components/DealersAndLocationsDropdown/hooks";
import { typedQueryClient } from "hooks";
import { ACL_ACTIVITIES, ACL_RESOURCES } from "models";
import { SnoozeButtonIcon } from "modules/LeadsDashboard/components";
import { LEASE_OPTIONS, useChecklistsQuestionsQuery, useLeaseCompanyOptions, useQuestionResultStatusOptions } from "modules/LeadsDashboard/hooks";
import "modules/LeadsDashboard/Leads/NotSnoozedLeads/components/NotSnoozedTable/NotSnoozedTable.scss";
import { useLeadsNotSnoozedFilters, useNotSnoozedLeadsTableFilterHandlers } from "modules/LeadsDashboard/Leads/NotSnoozedLeads/hooks";
import { QUESTION_STATUSES_DEFAULT_STATUSES } from "modules/LeadsDashboard/Leads/SnoozedLeads/hooks";
import { LeadsQueryKeys } from "modules/LeadsDashboard/queryKeys";
import { getFormattedDate, getLocationName } from "modules/LeadsDashboard/util";
import { PATHS } from "router/paths";

import type { AccessorColumnDef } from "@tanstack/react-table";
import type { Snooze } from "models";
import type { QuestionResultData } from "modules/AppointmentDetails/components/Checks/components/Check/components/QuestionResults/types";
import type { AddInterventionPayload } from "modules/AppointmentDetails/hooks";
import type { PossibleQuestionStatusOptions } from "modules/LeadsDashboard/hooks";
import type { Dispatch, SetStateAction } from "react";

type NotSnoozedLeadsTableProps = {
  setModalData: Dispatch<SetStateAction<AddInterventionPayload | QuestionResultData | null>>;
  isLoading?: boolean;
};

const getSelectedQuestionStatuses = (statuses: PossibleQuestionStatusOptions[] | null) => {
  if (statuses?.length === QUESTION_STATUSES_DEFAULT_STATUSES.length) return ["all"];
  return statuses?.map(String) ?? ["all"];
};

export const NotSnoozedLeadsTable = ({ setModalData, isLoading }: NotSnoozedLeadsTableProps) => {
  const { t } = useTranslation();

  const { locations } = usePermanentLocations();
  const { leaseCompanyOptions } = useLeaseCompanyOptions();
  const { questionResultStatusOptions } = useQuestionResultStatusOptions();
  const notSnoozedFilters = useLeadsNotSnoozedFilters();
  const canCreateSnooze = useCan(ACL_ACTIVITIES.Create, ACL_RESOURCES.Snooze);

  const { handleOnLeaseSelect, handlePaginationSelect, handleOnQuestionSelect, handleDateRangeChange, handleQuestionResultStatusSelect, handlePageSizeChange } =
    useNotSnoozedLeadsTableFilterHandlers();

  // TODO: clean this up, i can smell the workarounds, typedQueryClient.getQueryData does not trigger update on cache change
  const notSnoozedLeads = typedQueryClient.getQueryData(LeadsQueryKeys.listNotSnoozed);

  const nbItems = notSnoozedLeads?.nb_items || 0;
  const nbPages = notSnoozedLeads?.nb_pages || 1;
  const notSnoozedData = notSnoozedLeads?.items || [];
  const {
    date_from: dateFrom,
    date_to: dateTo,
    page: currentPage,
    page_size: pageSize,
    questions,
    is_lease_company,
    dealer_ids,
    location_ids,
    statuses
  } = notSnoozedFilters;
  const selectedQuestionStatuses = getSelectedQuestionStatuses(statuses);
  const { data: interventionQuestionOptions = [] } = useChecklistsQuestionsQuery({ dealer_ids, location_ids });
  const selectedLeaseCompanyOption = is_lease_company === null ? LEASE_OPTIONS.All : is_lease_company ? LEASE_OPTIONS.Lease : LEASE_OPTIONS.NoLease;

  const selectedQuestions = questions
    ? questions.reduce<string[]>((acc, questionId) => {
        const question = interventionQuestionOptions.find(element => element.filterValue === questionId);
        if (question) acc.push(question.key.toString());

        return acc;
      }, [])
    : ["all"];

  const columns = useMemo<AccessorColumnDef<Snooze, number>[]>(
    () => [
      {
        accessorFn: row => row.id,
        id: "appointment_date",
        header: () => (
          <div className="appointment-table-wrapper default-cursor NotSnoozedTable" onClick={e => e.stopPropagation()}>
            <span className="table-header-text no-wrap-text">{_startCase(t("v8_appointment_date", "Appointment Date"))} </span>
            <TableDatePicker dateStart={dateFrom} dateEnd={dateTo} onChange={handleDateRangeChange} inputClassName="appointment-date-not-snoozed-input" />
          </div>
        ),
        size: 180,
        cell: ({
          row: {
            original: { appointment }
          }
        }) => <span className="ellipsis ellipsis-wo-nr">{getFormattedDate(appointment?.time_car_app)}</span>
      },

      {
        accessorFn: row => row.id,
        id: "wo",
        header: () => <span className="table-header-text no-wrap-text default-cursor">WO # {nbItems}</span>,
        size: 70,
        cell: ({
          row: {
            original: { appointment }
          }
        }) => {
          if (appointment?.wo_nr === "0") return "";

          return (
            <p className="ellipsis ">
              <Link to={`${PATHS.APPOINTMENTS}/${appointment?.id}`}>
                <Icon className="arrow up right from square" />
                {appointment?.wo_nr}
              </Link>
            </p>
          );
        }
      },

      {
        accessorFn: row => row.id,
        id: "reg_number",
        header: () => (
          <div className="appointment-table-wrapper default-cursor">
            <TableDropdownSelector
              trigger={<span className="ellipsis ellipsis-wo-nr table-header-text no-wrap-text"> {t("v8_reg_number", "Reg #")}</span>}
              options={leaseCompanyOptions}
              selected={[selectedLeaseCompanyOption]}
              handleOnSelect={handleOnLeaseSelect}
            />
          </div>
        ),
        size: 70,
        cell: ({
          row: {
            original: { appointment }
          }
        }) => {
          if (!appointment || !appointment.reg_number) return "";
          const { car_id, reg_number, is_lease_company } = appointment;

          return (
            <p className="ellipsis ellipsis-wo-nr">
              <Link to={`${PATHS.CAR_DETAIL}/${car_id}`}>
                <Icon className="arrow up right from square" />
                {reg_number}
                {is_lease_company && <Icon name="building" />}
              </Link>
            </p>
          );
        }
      },

      {
        accessorFn: row => row.id,
        id: "location_name",
        header: () => <span className="no-wrap-text table-header-text default-cursor">{t("v8_location", "Location")}</span>,
        size: 120,
        cell: ({
          row: {
            original: { dealer_location_id }
          }
        }) => <span className="ellipsis ellipsis-wo-nr">{getLocationName(dealer_location_id, locations)}</span>
      },

      {
        accessorFn: row => row.id,
        id: "intervention_question_result",
        header: () => (
          <div className="appointment-table-wrapper default-cursor">
            <TableDropdownSelector
              multiple={true}
              withSearch={true}
              trigger={<span className="table-header-text no-wrap-text"> {t("v8_intervention_question_result", "Intervention/Question Result")}</span>}
              selected={selectedQuestions}
              options={interventionQuestionOptions}
              handleOnSelect={handleOnQuestionSelect}
            />
          </div>
        ),
        size: 210,
        cell: ({
          row: {
            original: { question_result, intervention }
          }
        }) => <span>{question_result?.title || intervention?.title || ""}</span>
      },

      {
        accessorFn: row => row.id,
        id: "note",
        header: () => <span className="no-wrap-text table-header-text default-cursor"> {t("v8_note", "Note")} </span>,
        size: 350,
        cell: ({
          row: {
            original: { question_result, intervention, note }
          }
        }) => {
          const content = question_result?.raw || intervention?.description || note || "";
          return <Popup hoverable content={content} trigger={<p className="ellipsis ellipsis-wo-nr">{content}</p>} />;
        }
      },

      {
        accessorFn: row => row.id,
        id: "status",
        header: () => (
          <div className="table-action-buttons-container default-cursor">
            <TableDropdownSelector
              multiple={true}
              selected={selectedQuestionStatuses}
              options={questionResultStatusOptions}
              handleOnSelect={handleQuestionResultStatusSelect}
              trigger={<TableQuestionStatusButton selectedQuestionStatuses={selectedQuestionStatuses} />}
              scrolling={false}
              showFilterIcon={false}
            />
            <div className="table-icon-placeholder-div" />
            <div className="table-icon-placeholder-div" />
          </div>
        ),
        size: 30,
        cell: ({
          row: {
            original: {
              appointment,
              question_result,
              question_result_id,
              snooze_type_id,
              snooze_status_id,
              snooze_department_id,
              snooze_date,
              appointment_id,
              intervention_id,
              intervention,
              car_id,
              user,
              car
            }
          }
        }) => {
          const snoozeModalData: QuestionResultData = {
            title: "",
            description: "",
            solution: "",
            id: -1,
            images: [],
            appointment_id: appointment_id,
            snoozed: question_result?.snoozed || intervention?.snoozed || false,
            snooze_history: question_result?.snooze_history || intervention?.snooze_history || [],
            snooze_type_id: snooze_type_id,
            note: question_result?.note,
            car_id,
            car,
            user,
            intervention,
            appointment,
            ...(question_result && { question_result }),
            ...(question_result_id && { question_result_id }),
            ...(snooze_status_id && { snooze_status_id }),
            ...(snooze_department_id && { snooze_department_id }),
            ...(snooze_date && { snooze_date }),
            ...(intervention_id && { intervention_id })
          };

          return (
            <div className="table-action-buttons-container">
              <QuestionStatusIcon status={question_result?.status ?? intervention?.question_result_status ?? null} />
              {appointment?.wo_nr !== "0" ? (
                <CustomerCard
                  contractor={appointment?.customer_contract}
                  driver={appointment?.customer_driver}
                  owner={appointment?.customer_owner}
                  driverOptedOutFromImporting={appointment?.driver_opted_out_from_importing}
                  ownerOptedOutFromImporting={appointment?.owner_opted_out_from_importing}
                  contractorOptedOutFromImporting={appointment?.contractor_opted_out_from_importing}
                />
              ) : (
                <div className="table-icon-placeholder-div" />
              )}
              <SnoozeButtonIcon disabled={!canCreateSnooze} isSnoozed={Boolean(snooze_date)} handleClick={() => setModalData(snoozeModalData)} />
            </div>
          );
        }
      }
    ],
    [
      t,
      locations,
      setModalData,
      nbItems,
      dateFrom,
      dateTo,
      leaseCompanyOptions,
      questionResultStatusOptions,
      interventionQuestionOptions,
      selectedQuestions,
      selectedLeaseCompanyOption,
      selectedQuestionStatuses,
      handleOnLeaseSelect,
      handleOnQuestionSelect,
      handleDateRangeChange,
      handleQuestionResultStatusSelect
    ]
  );

  return (
    <div className="table-container">
      <ReactTable
        isFetching={isLoading}
        columns={columns}
        data={notSnoozedData}
        pageCount={nbPages}
        pageSize={pageSize}
        totalItems={nbItems}
        pagination={nbPages > 1 ? { pageIndex: currentPage - 1, pageSize } : undefined}
        onPaginationChange={handlePaginationSelect}
        onChangePageSize={handlePageSizeChange}
      />
    </div>
  );
};
